import React, { useState, useEffect, useRef, useMemo } from "react";
import { withRouter } from "react-router";
import useCheckMobileScreen from "../hooks/useCheckMobileScreen";
import customFetch from "./apicall/api";

const SupersetDashboardEmbed = ({
  supersetId,
  supersetUrl,
  supersetInputData,
  api_token,
  report_id,
  setLoading,
  affinityDict,
}) => {
  const supersetIdContainer = useRef(null);
  const supersetUrlContainer = useRef(null);
  const [supersetUrlContainerHeight, setSupersetUrlContainerHeight] = useState('100vh');
  // const isMobile = useCheckMobileScreen();

  const empID = localStorage.getItem("employee_id");
  const userID = localStorage.getItem("in_userid");
  const designationID = localStorage.getItem("e_designation_id");
  const branchID = localStorage.getItem("branch_id");

  const urlParams = useMemo(() => ({
    in_userid: userID,
    in_emp_id: empID,
    in_des_id: designationID,
    in_branch_id: branchID,
    in_report_id: report_id,
    ...supersetInputData,
    ...affinityDict,
  }), [userID, empID, designationID, branchID, report_id, supersetInputData, affinityDict]);

  useEffect(() => {
    if (!supersetUrl) return;
    setLoading(true);
    if (supersetUrlContainer.current) {
      const fromTop = supersetUrlContainer.current.getBoundingClientRect().top;
      setSupersetUrlContainerHeight(`calc(100vh - ${fromTop}px)`);
    }
  }, [supersetUrl]);

  useEffect(() => {
    if (!supersetId) return;

    const embedDashboard = async () => {
      try {
        setLoading(true);

        // Handle reportCategories style (for mobile)
        const reportCatDiv = document.getElementById('reportCategories');
        const reportStyle = reportCatDiv ? reportCatDiv.getAttribute('style') : '';

        if (window.innerWidth <= 768 && reportCatDiv) {
          reportCatDiv.setAttribute('style', 'max-width: 100%;');
        }

        // Handle viewport meta tag
        const metaViewport = document.querySelector('meta[name="viewport"]');
        const originalContent = metaViewport ? metaViewport.getAttribute('content') : '';

        if (metaViewport) {
          metaViewport.setAttribute('content', '');
        }

        const entityID = await fetchEntityID();
        const tokenData = await fetchToken();

        // Check if token fetch was successful before proceeding
        if (!tokenData || !tokenData.token) {
          console.error("Failed to fetch guest token");
          setLoading(false);
          return;
        }

        const fullUrlParams = {
          ...urlParams,
          in_entity_id: entityID,
          in_guest_token: tokenData.token,
        };

        if (tokenData && supersetIdContainer.current) {
          window.supersetEmbeddedSdk.embedDashboard({
            id: supersetId,
            supersetDomain: process.env.REACT_APP_SUPERSET_URL,
            mountPoint: supersetIdContainer.current,
            fetchGuestToken: () => tokenData.token,
            dashboardUiConfig: {
              hideTitle: true,
              filters: { expanded: false },
              urlParams: fullUrlParams,
            },
          });

          const iframe = supersetIdContainer.current.querySelector("iframe");
          if (iframe) {
            const fromTop = iframe.getBoundingClientRect();
            iframe.style.width = "100%";
            iframe.style.height = `calc(100vh - ${fromTop.top}px)`;
            // adjustZoom();
            // window.addEventListener("resize", adjustZoom);
          }
        }

        return () => {
          if (metaViewport) {
            metaViewport.setAttribute('content', originalContent || 'width=device-width, initial-scale=1.0');
          }
          if (reportCatDiv) {
            reportCatDiv.setAttribute('style', reportStyle);
          }
        };
      } catch (e) {
        console.error("Error embedding dashboard:", e);
      } finally {
        setLoading(false);
      }
    };

    embedDashboard();
  }, [supersetId, urlParams]);

  const supersetUrlWithParams = useMemo(() => {
    if (!supersetUrl) return "";
    const url = new URL(supersetUrl);
    Object.entries(urlParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, value);
      }
    });
    return url.toString();
  }, [supersetUrl, urlParams]);

  const fetchEntityID = async () => {
    try {
      const queryParams = new URLSearchParams({ user_id: userID }).toString();
      const response = await customFetch(
        `${process.env.REACT_APP_URL}/fetch/entity?${queryParams}`,
        {
          method: "GET",
          headers: { "api-token": api_token },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch entity ID");
      const data = await response.json();
      return data["entity_id"];
    } catch (error) {
      console.error("Error fetching entity ID:", error);
      return "";
    }
  };

  const fetchAccessToken = async () => {
    try {
      const body = {
        username: process.env.REACT_APP_SUPERSET_USERID,
        password: process.env.REACT_APP_SUPERSET_PASSWORD,
        provider: "db",
        refresh: true,
      };

      const response = await fetch(
        `${process.env.REACT_APP_SUPERSET_URL}api/v1/security/login`,
        {
          method: "POST",
          body: JSON.stringify(body),
          headers: { "Content-Type": "application/json" },
        }
      );
      const json = await response.json();
      return json.access_token;
    } catch (e) {
      console.error("Error fetching access token:", e);
    }
  };

  const fetchGuestToken = async (accessToken) => {
    try {
      const body = {
        user: {
          // first_name: "Embed",
          // last_name: "Embed",
          username: process.env.REACT_APP_SUPERSET_USERID
        },
        resources: [{ id: supersetId, type: "dashboard" }],
        rls: [
          // {
          //   "clause": "string",
          //   "dataset": 0
          // }
        ],
      };
      const response = await fetch(
        `${process.env.REACT_APP_SUPERSET_URL}api/v1/security/guest_token/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(body),
        }
      );
      return await response.json();
    } catch (e) {
      console.error("Error fetching guest token:", e);
    }
  };

  const fetchToken = async () => {
    const accessToken = await fetchAccessToken();
    return await fetchGuestToken(accessToken);
  };

  // const adjustZoom = () => {
  //   if (window.innerWidth < 768) {
  //     const scale = window.innerWidth / 1024;
  //     document.body.style.transform = `scale(${scale})`;
  //     document.body.style.transformOrigin = "0 0";
  //     // document.body.style.maxWidth = 1024
  //     document.body.style.width = `${100 / scale}%`;
  //   } else {
  //     // document.body.style.transform = "";
  //     // document.body.style.width = "";
  //   }
  // };

  return (
    <React.Fragment>
      {/* {isMobile && <div>View in landscape mode</div>} */}
      {(supersetId && supersetId !== "") && (
        <div className="superset-container" ref={supersetIdContainer}></div>
      )}
      {(supersetUrl && supersetUrl !== "") && (
        <iframe
          ref={supersetUrlContainer}
          src={supersetUrlWithParams}
          style={{ width: "100%", height: supersetUrlContainerHeight }}
          onLoad={() => setLoading(false)}
        ></iframe>
      )}
    </React.Fragment>
  );
};

export default withRouter(SupersetDashboardEmbed);
